import qs from '@scoir/query-string'

import api from 'common/api'
import { getTrackEventsSessionId } from 'common/get-track-events-session-id'
import { clearLocalStorage, routeToApp, setCookies } from 'common/login-service'
import { useClickTrackingQueryParamsString } from 'components/QueryParamsProvider'

import { RegisterResponse } from './types'

type StudentInviteResponse = {
  Email: string
  FirstName: string
  HighSchool?: {
    City: string
    State: string
    HighSchoolName: string
    CleverRequired: boolean
    ClassLinkEnabled: boolean
    ClassLinkRequired: boolean
    SISSyncEnabled: boolean
  }
  MatchedHighSchoolStudent?: {
    FirstName: string
    LastName: string
  }
  Token: string
  IsMiddleSchoolStudent?: boolean
  GoogleSSORequired: boolean
}

const decodeToken = (token: string) => {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace('-', '+').replace('_', '/')
  return JSON.parse(window.atob(base64))
}

export const validateStudentInvite = async (
  token: string,
  setError: any,
  setInitialValues: any,
  setLoading: any,
  setTokenAuth: any,
  setUser: any,
) => {
  api
    .post<unknown, StudentInviteResponse>(`/api/student/register/invite/validate/${token}`, {})
    .then((auth) => {
      const isMS = decodeToken(token).IsMiddleSchoolStudent
      auth.IsMiddleSchoolStudent = isMS || false
      setTokenAuth(auth)
      // An invitation that names no roster row, or no school, still validates -
      // self-registration produces both - so nothing here may assume either
      setUser({
        name: auth.MatchedHighSchoolStudent?.FirstName ?? '',
        location: auth.HighSchool ? `${auth.HighSchool.City}, ${auth.HighSchool.State}` : '',
        highschool: auth.HighSchool?.HighSchoolName,
        cleverRequired: auth.HighSchool?.CleverRequired,
        cleverSyncEnabled: auth.HighSchool?.SISSyncEnabled,
        classLinkEnabled: auth.HighSchool?.ClassLinkEnabled,
        classLinkRequired: auth.HighSchool?.ClassLinkRequired,
        googleRequired: auth.GoogleSSORequired,
        email: auth.Email,
      })
      setInitialValues({
        password: {
          pass: '',
          passConfirm: '',
        },
        confirmTOS: false,
        recaptchaResponse: '',
      })
      setLoading(false)
    })
    .catch((err) => {
      // Programming errors land here too, so say which one before the page
      // reports the invitation itself as invalid
      console.error('Could not validate the student invitation', err)
      setError(true)
      setLoading(false)
    })
}

const registerStudentUser = async (
  values: any,
  tokenAuth: any,
  setConfirmError: any,
  setSubmitting: any,
  additionalParams: any,
) => {
  // An invitation naming no roster row still carries the student's own details,
  // so the row may fill these in but must never blank them
  const rosterRow = tokenAuth.MatchedHighSchoolStudent
  const body: { [key: string]: any } = {
    ...tokenAuth,
    Category: 'High School Student',
    ...(rosterRow && {
      FirstName: rosterRow.FirstName,
      HighSchoolStudentId: rosterRow.HighSchoolStudentId,
      LastName: rosterRow.LastName,
      GraduationYear: rosterRow.GraduationYear,
    }),
    Password: {
      Pass: values.password.pass,
      PassConfirm: values.password.passConfirm,
    },
    RecaptchaResponse: values.recaptchaResponse,
  }
  const params = qs.stringify({
    ...additionalParams,
    sessionId: getTrackEventsSessionId(),
  })

  api
    .post<unknown, RegisterResponse>(`/api/student/confirm?${params}`, body)
    .then((loginResponse) => {
      if (loginResponse.access_token && loginResponse.UserTypes) {
        const userType = loginResponse.UserTypes[0]
        if (!userType) {
          console.warn('No user type returned on password reset, cannot log in automatically')
          return
        }
        clearLocalStorage(userType)
        setCookies(userType, loginResponse.access_token)
        routeToApp(userType)
      } else {
        setConfirmError(true)
        setSubmitting(false)
      }
    })
    .catch(() => {
      setConfirmError(true)
      setSubmitting(false)
    })
}

export const useRegisterStudentUser = () => {
  const params = useClickTrackingQueryParamsString()
  return async (values: any, tokenAuth: any, setConfirmError: any, setSubmitting: any) => {
    return await registerStudentUser(values, tokenAuth, setConfirmError, setSubmitting, params)
  }
}
